import { api } from '@/api';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { readAdminBooking, readAdminBookings, type AdminBookingFilters } from '@/api/core/bookings';
import {
  readAdminOrderDetail,
  readAdminPaymentDetail,
  type AdminOrderDetail,
  type AdminPaymentDetail,
} from '@/api/core/finance';
import { readFinancialReconciliation } from '@/api/core/financial-reconciliation';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { operatorConfig, operatorSession } from '@/auth/runtime';
import type { OperatorSession } from '@/auth/session';

export interface BookingRuntime {
  client: ServiceClient;
  session: OperatorSession;
  apiNamespace: string;
}

export const bookingRuntime: BookingRuntime = {
  client: api.core,
  session: operatorSession,
  apiNamespace: operatorConfig?.coreApiUrl ?? 'unconfigured',
};

export function hasDomainAccess(
  session: OperatorSession,
  generation: number,
  domain: string,
  requiredBits = 1,
): boolean {
  const state = session.getSnapshot();
  return (
    state.generation === generation &&
    state.status === 'authorized' &&
    !!state.authorization &&
    !state.deniedDomains.includes(domain) &&
    canReadDomain(state.authorization, domain, requiredBits)
  );
}

async function readDomain<T>(
  runtime: BookingRuntime,
  generation: number,
  domain: string,
  requiredBits: number,
  signal: AbortSignal,
  read: () => Promise<T>,
): Promise<T> {
  if (!hasDomainAccess(runtime.session, generation, domain, requiredBits))
    throw new Error('현재 조회 권한을 확인할 수 없습니다.');
  try {
    const result = await read();
    if (!hasDomainAccess(runtime.session, generation, domain, requiredBits))
      throw new Error('현재 조회 권한을 확인할 수 없습니다.');
    return result;
  } catch (error) {
    if (
      isApiError(error) &&
      error.status === 403 &&
      !signal.aborted &&
      hasDomainAccess(runtime.session, generation, domain, requiredBits)
    )
      runtime.session.reportDomainAccessDenied(domain, generation);
    throw error;
  }
}

export function bookingListQuery(
  runtime: BookingRuntime,
  generation: number,
  filters: AdminBookingFilters,
  enabled: boolean,
) {
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'bookings'), filters],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readDomain(runtime, generation, 'booking', 2, signal, () =>
        readAdminBookings(runtime.client, filters, signal),
      ),
    enabled: enabled && hasDomainAccess(runtime.session, generation, 'booking', 2),
    meta: { suppressGlobalError: true },
  };
}

export function bookingDetailQuery(
  runtime: BookingRuntime,
  generation: number,
  bookingId: string,
  enabled: boolean,
) {
  return {
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'booking-detail', bookingId)],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readDomain(runtime, generation, 'booking', 2, signal, () =>
        readAdminBooking(runtime.client, bookingId, signal),
      ),
    enabled: enabled && hasDomainAccess(runtime.session, generation, 'booking', 2),
    meta: { suppressGlobalError: true },
  };
}

export function orderDetailQuery(
  runtime: BookingRuntime,
  generation: number,
  bookingId: string,
  orderId: string,
  enabled: boolean,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'order-detail', bookingId),
      orderId,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readDomain<AdminOrderDetail>(runtime, generation, 'order', 1, signal, () =>
        readAdminOrderDetail(runtime.client, orderId, signal),
      ),
    enabled: enabled && hasDomainAccess(runtime.session, generation, 'order', 1),
    meta: { suppressGlobalError: true },
  };
}

export function paymentDetailQuery(
  runtime: BookingRuntime,
  generation: number,
  bookingId: string,
  orderId: string,
  paymentId: string,
  enabled: boolean,
) {
  return {
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'payment-detail', bookingId),
      orderId,
      paymentId,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readDomain<AdminPaymentDetail>(runtime, generation, 'payment', 1, signal, () =>
        readAdminPaymentDetail(runtime.client, paymentId, signal),
      ),
    enabled: enabled && hasDomainAccess(runtime.session, generation, 'payment', 1),
    meta: { suppressGlobalError: true },
  };
}

export function financialReconciliationQuery(
  runtime: BookingRuntime,
  generation: number,
  bookingId: string,
  orderId: string,
) {
  const queryKey = [
    ...coreQueryKey(runtime.apiNamespace, generation, 'financial-reconciliation', bookingId),
    orderId,
  ] as const;
  return {
    queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (
        !hasDomainAccess(runtime.session, generation, 'order', 1) ||
        !hasDomainAccess(runtime.session, generation, 'payment', 1)
      )
        throw new Error('현재 Order와 Payment 조회 권한을 모두 확인할 수 없습니다.');
      const result = await readFinancialReconciliation(runtime.client, orderId, signal);
      if (
        !hasDomainAccess(runtime.session, generation, 'order', 1) ||
        !hasDomainAccess(runtime.session, generation, 'payment', 1)
      )
        throw new Error('현재 Order와 Payment 조회 권한을 모두 확인할 수 없습니다.');
      return result;
    },
    enabled: false,
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { suppressGlobalError: true },
  };
}
